import { Component } from '@theme/component';

/**
 * Estimated delivery timeline with urgency flip-clock countdown.
 *
 * Features:
 *   - Business-day-aware date calculations (skip weekends + public holidays)
 *   - Configurable same-day cutoff hour
 *   - Split-flap flip-clock countdown animation (CSS 3D transforms)
 *   - Three urgency states: countdown / after-cutoff / non-business-day
 *   - All text from translation keys (no hardcoded UI strings)
 *
 * Date format tokens (from data-date-format):
 *   DD    → day of month       (e.g. 16)
 *   MM    → zero-padded month  (e.g. 03)
 *   Mon   → short month name   (e.g. Mar.)
 *   Month → full month name    (e.g. Martie)
 */
class EstimatedDeliveryComponent extends Component {
  requiredRefs = ['stepsContainer'];

  /** @type {string[]} */
  #MONTH_KEYS = [
    'jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
  ];

  /** @type {number|null} Timer ID for countdown */
  #countdownTimer = null;

  /** @type {Record<string, HTMLElement|null>} Flip digit card elements */
  #flipDigits = {};

  /** @type {Record<string, string>} Previous digit values for flip detection */
  #prevDigits = {};

  /** @type {Record<string, {top: HTMLElement, bot: HTMLElement}>} Cached inner text spans */
  #flipTextRefs = {};

  /** @type {number} Cached cutoff hour for visibility resume */
  #cutoffHour = 15;

  /** Bound handler for Page Visibility API */
  #handleVisibility = () => {
    if (document.hidden) {
      this.#clearCountdown();
    } else {
      this.#startCountdown(this.#cutoffHour);
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#computeAndRender();
    document.addEventListener('visibilitychange', this.#handleVisibility);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearCountdown();
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  /* ─── Main orchestrator ─── */

  #computeAndRender() {
    const cutoffHour = parseInt(this.dataset.cutoffHour, 10) || 15;
    const processingDays = parseInt(this.dataset.processingDays, 10) || 0;
    const shippingDaysMin = parseInt(this.dataset.shippingDaysMin, 10) || 1;
    const shippingDaysMax = parseInt(this.dataset.shippingDaysMax, 10) || 2;
    const skipWeekends = this.dataset.skipWeekends !== 'false';
    const dateFormat = this.dataset.dateFormat || 'DD Month';
    const showUrgency = this.dataset.showUrgency !== 'false';
    const holidays = this.#parseHolidays(this.dataset.holidays);

    const now = new Date();
    const orderDate = new Date(now);
    const shipDate = new Date(now);

    const isWeekend = skipWeekends && this.#isWeekendDay(now);
    const isTodayHoliday = this.#isHoliday(now, holidays);
    const isNonBusinessDay = isWeekend || isTodayHoliday;
    const isPastCutoff = now.getHours() >= cutoffHour;

    if (isNonBusinessDay) {
      this.#skipToBusinessDay(shipDate, skipWeekends, holidays);
      if (processingDays > 0) {
        this.#addBusinessDays(shipDate, processingDays, skipWeekends, holidays);
      }
    } else if (isPastCutoff) {
      this.#addBusinessDays(shipDate, 1, skipWeekends, holidays);
      if (processingDays > 0) {
        this.#addBusinessDays(shipDate, processingDays, skipWeekends, holidays);
      }
    } else {
      if (processingDays > 0) {
        this.#addBusinessDays(shipDate, processingDays, skipWeekends, holidays);
      }
    }

    const deliveryMin = new Date(shipDate);
    this.#addBusinessDays(deliveryMin, shippingDaysMin, skipWeekends, holidays);

    const deliveryMax = new Date(shipDate);
    this.#addBusinessDays(deliveryMax, shippingDaysMax, skipWeekends, holidays);

    /* Update timeline dates */
    const orderEl = this.refs.orderDate;
    const processEl = this.refs.processDate;
    const deliveryEl = this.refs.deliveryDate;

    if (orderEl) orderEl.textContent = this.#formatDate(orderDate, dateFormat);
    if (processEl) processEl.textContent = this.#formatDate(shipDate, dateFormat);

    if (deliveryEl) {
      deliveryEl.textContent = deliveryMin.getTime() === deliveryMax.getTime()
        ? this.#formatDate(deliveryMin, dateFormat)
        : this.#formatDateRange(deliveryMin, deliveryMax, dateFormat);
    }

    /* Urgency banner */
    if (showUrgency) {
      this.#renderUrgency(now, cutoffHour, shipDate, dateFormat, isNonBusinessDay, isPastCutoff);
    }
  }

  /* ─── Holiday helpers ─── */

  /**
   * Parses a comma-separated DD.MM holiday string into an array.
   * @param {string|undefined} str
   * @returns {Array<{day: number, month: number}>}
   */
  #parseHolidays(str) {
    if (!str) return [];
    return str.split(',').map(s => s.trim()).filter(Boolean).map(s => {
      const parts = s.split('.');
      return { day: parseInt(parts[0], 10), month: parseInt(parts[1], 10) };
    }).filter(h => h.day > 0 && h.month > 0);
  }

  /**
   * @param {Date} date
   * @param {Array<{day: number, month: number}>} holidays
   * @returns {boolean}
   */
  #isHoliday(date, holidays) {
    const d = date.getDate();
    const m = date.getMonth() + 1;
    return holidays.some(h => h.day === d && h.month === m);
  }

  /** @param {Date} date */
  #isWeekendDay(date) {
    return date.getDay() === 0 || date.getDay() === 6;
  }

  /* ─── Business-day arithmetic ─── */

  /**
   * Advances `date` to the next business day (skipping weekends + holidays).
   * @param {Date} date
   * @param {boolean} skipWeekends
   * @param {Array<{day: number, month: number}>} holidays
   */
  #skipToBusinessDay(date, skipWeekends, holidays) {
    do {
      date.setDate(date.getDate() + 1);
    } while (
      (skipWeekends && this.#isWeekendDay(date)) ||
      this.#isHoliday(date, holidays)
    );
  }

  /**
   * Adds N business days to a date in-place.
   * @param {Date} date
   * @param {number} days
   * @param {boolean} skipWeekends
   * @param {Array<{day: number, month: number}>} holidays
   */
  #addBusinessDays(date, days, skipWeekends, holidays) {
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      if (skipWeekends && this.#isWeekendDay(date)) continue;
      if (this.#isHoliday(date, holidays)) continue;
      added++;
    }
  }

  /* ─── Urgency banner ─── */

  /**
   * @param {Date} now
   * @param {number} cutoffHour
   * @param {Date} shipDate
   * @param {string} dateFormat
   * @param {boolean} isNonBusinessDay
   * @param {boolean} isPastCutoff
   */
  #renderUrgency(now, cutoffHour, shipDate, dateFormat, isNonBusinessDay, isPastCutoff) {
    const banner = this.refs.urgencyBanner;
    if (!banner) return;

    const messageEl = this.refs.urgencyMessage;
    const countdownRow = this.refs.urgencyCountdownRow;
    if (!messageEl) return;

    if (isNonBusinessDay || isPastCutoff) {
      /* — Ship date is NOT today — */
      const dayMonth = `${shipDate.getDate()}.${String(shipDate.getMonth() + 1).padStart(2, '0')}`;
      const tpl = this.dataset.urgencyNotToday || 'Order now and your order ships on {date}!';
      messageEl.textContent = tpl.replace('{date}', dayMonth);
      if (countdownRow) countdownRow.hidden = true;
      banner.removeAttribute('hidden');
      banner.dataset.state = 'info';
    } else {
      /* — Before cutoff: show countdown — */
      const cutoffStr = `${String(cutoffHour).padStart(2, '0')}:00`;
      this.#renderUrgencyMessage(messageEl, this.dataset.urgencyBefore || 'Order before {cutoff} and your order ships today!', cutoffStr);

      if (countdownRow) {
        countdownRow.hidden = false;
        this.#setupCountdownRow(cutoffHour);
      }

      banner.removeAttribute('hidden');
      banner.dataset.state = 'countdown';
    }
  }

  /**
   * Builds the urgency message with a styled cutoff-time span.
   * @param {HTMLElement} container
   * @param {string} template
   * @param {string} cutoffStr
   */
  #renderUrgencyMessage(container, template, cutoffStr) {
    container.textContent = '';

    const parts = template.split('{cutoff}');
    if (parts.length === 1) {
      container.textContent = template;
      return;
    }

    container.appendChild(document.createTextNode(parts[0]));

    const timeSpan = document.createElement('span');
    timeSpan.className = 'ed-urgency__cutoff-time';
    timeSpan.textContent = cutoffStr;
    container.appendChild(timeSpan);

    if (parts[1]) {
      container.appendChild(document.createTextNode(parts[1]));
    }
  }

  /**
   * Sets up the countdown row: label prefix, flip clock, label suffix.
   * @param {number} cutoffHour
   */
  #setupCountdownRow(cutoffHour) {
    const prefixEl = this.refs.urgencyCountdownPrefix;
    const suffixEl = this.refs.urgencyCountdownSuffix;
    const clockEl = this.refs.flipClock;
    if (!clockEl) return;

    const label = this.dataset.urgencyCountdown || '{time} left!';
    const [prefix, suffix] = label.split('{time}');

    if (prefixEl) prefixEl.textContent = prefix || '';
    if (suffixEl) suffixEl.textContent = suffix || '';

    this.#buildFlipClock(clockEl);
    this.#startCountdown(cutoffHour);
  }

  /* ─── Flip clock ─── */

  /**
   * Creates the flip-clock DOM structure inside the container.
   * Layout: [H1][H2] : [M1][M2] : [S1][S2]
   * @param {HTMLElement} container
   */
  #buildFlipClock(container) {
    /* Only build once; skip if already populated */
    if (container.childElementCount > 0) return;

    this.#flipDigits = {};
    this.#prevDigits = {};
    this.#flipTextRefs = {};

    const groups = [
      { keys: ['h1', 'h2'], label: 'h' },
      { keys: ['m1', 'm2'], label: 'm' },
      { keys: ['s1', 's2'], label: 's' },
    ];

    groups.forEach((group, gi) => {
      if (gi > 0) {
        const sep = document.createElement('span');
        sep.className = 'ed-fc__sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = ':';
        container.appendChild(sep);
      }

      const groupEl = document.createElement('span');
      groupEl.className = 'ed-fc__group';

      group.keys.forEach(key => {
        const card = this.#createDigitCard();
        groupEl.appendChild(card);
        this.#flipDigits[key] = card;
        this.#prevDigits[key] = '0';
        this.#flipTextRefs[key] = {
          top: card.querySelector('.ed-fc-d__top span'),
          bot: card.querySelector('.ed-fc-d__bot span'),
        };
      });

      container.appendChild(groupEl);
    });
  }

  /**
   * Creates a single flip-digit card element.
   * @returns {HTMLElement}
   */
  #createDigitCard() {
    const card = document.createElement('span');
    card.className = 'ed-fc-d';

    const top = document.createElement('span');
    top.className = 'ed-fc-d__top';
    const topText = document.createElement('span');
    topText.textContent = '0';
    top.appendChild(topText);

    const bot = document.createElement('span');
    bot.className = 'ed-fc-d__bot';
    const botText = document.createElement('span');
    botText.textContent = '0';
    bot.appendChild(botText);

    card.appendChild(top);
    card.appendChild(bot);
    return card;
  }

  /**
   * Starts the live countdown timer, updating the flip clock every second.
   * Pauses automatically when the tab is hidden (via visibilitychange).
   * @param {number} cutoffHour
   */
  #startCountdown(cutoffHour) {
    this.#clearCountdown();
    this.#cutoffHour = cutoffHour;

    const tick = () => {
      const now = new Date();
      const cutoff = new Date(now);
      cutoff.setHours(cutoffHour, 0, 0, 0);

      const diff = cutoff - now;
      if (diff <= 0) {
        this.#clearCountdown();
        this.#computeAndRender();
        return;
      }

      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      this.#updateFlipClock(h, m, s);
    };

    tick();
    this.#countdownTimer = setInterval(tick, 1000);
  }

  /**
   * Pushes new time values to all six digit cards.
   * @param {number} hours
   * @param {number} minutes
   * @param {number} seconds
   */
  #updateFlipClock(hours, minutes, seconds) {
    const h = String(hours).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    const s = String(seconds).padStart(2, '0');

    this.#flipDigit('h1', h[0]);
    this.#flipDigit('h2', h[1]);
    this.#flipDigit('m1', m[0]);
    this.#flipDigit('m2', m[1]);
    this.#flipDigit('s1', s[0]);
    this.#flipDigit('s2', s[1]);
  }

  /**
   * Updates a single digit card with split-flap animation.
   * @param {string} key   – digit identifier (e.g. "h1", "m2")
   * @param {string} value – new single digit character
   */
  #flipDigit(key, value) {
    const card = this.#flipDigits[key];
    if (!card) return;

    const old = this.#prevDigits[key];
    if (old === value) return;
    this.#prevDigits[key] = value;

    const refs = this.#flipTextRefs[key];
    if (!refs) return;
    const { top: topText, bot: botText } = refs;

    /* Set static top to new value (hidden under the flap initially) */
    topText.textContent = value;

    /* ── Top flap: shows OLD value's upper half, folds down ── */
    const flapTop = document.createElement('span');
    flapTop.className = 'ed-fc-d__flap-top';
    const flapTopInner = document.createElement('span');
    flapTopInner.textContent = old;
    flapTop.appendChild(flapTopInner);
    card.appendChild(flapTop);

    /* ── Bottom flap: shows NEW value's lower half, unfolds ── */
    const flapBot = document.createElement('span');
    flapBot.className = 'ed-fc-d__flap-bot';
    const flapBotInner = document.createElement('span');
    flapBotInner.textContent = value;
    flapBot.appendChild(flapBotInner);
    card.appendChild(flapBot);

    /* Update static bottom when top flap finishes (mid-flip) */
    flapTop.addEventListener('animationend', () => {
      botText.textContent = value;
    }, { once: true });

    /* Clean up both flaps when bottom flap finishes */
    flapBot.addEventListener('animationend', () => {
      flapTop.remove();
      flapBot.remove();
    }, { once: true });
  }

  /** Clears the countdown interval if active. */
  #clearCountdown() {
    if (this.#countdownTimer !== null) {
      clearInterval(this.#countdownTimer);
      this.#countdownTimer = null;
    }
  }

  /* ─── Date formatting ─── */

  /** @returns {string} Short month name from data attributes */
  #getMonthShort(date) {
    const key = this.#MONTH_KEYS[date.getMonth()];
    return this.dataset[`month${key.charAt(0).toUpperCase()}${key.slice(1)}`] || key;
  }

  /** @returns {string} Full month name from data attributes */
  #getMonthFull(date) {
    const key = this.#MONTH_KEYS[date.getMonth()];
    return this.dataset[`monthFull${key.charAt(0).toUpperCase()}${key.slice(1)}`] || this.#getMonthShort(date);
  }

  #formatDate(date, format) {
    const dd = String(date.getDate());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const mon = this.#getMonthShort(date);
    const month = this.#getMonthFull(date);

    return format
      .replace('DD', dd)
      .replace('MM', mm)
      .replace('Month', month)
      .replace('Mon', mon);
  }

  #formatDateRange(dateMin, dateMax, format) {
    const sameMonth = dateMin.getMonth() === dateMax.getMonth();

    if (sameMonth && (format === 'DD Month' || format === 'DD Mon')) {
      const suffix = format === 'DD Month'
        ? this.#getMonthFull(dateMin)
        : this.#getMonthShort(dateMin);
      return `${dateMin.getDate()} - ${dateMax.getDate()} ${suffix}`;
    }

    if (sameMonth && format === 'DD.MM') {
      const m = String(dateMin.getMonth() + 1).padStart(2, '0');
      return `${dateMin.getDate()}.${m} - ${dateMax.getDate()}.${m}`;
    }

    if (sameMonth && (format === 'Month DD' || format === 'Mon DD')) {
      const prefix = format === 'Month DD'
        ? this.#getMonthFull(dateMin)
        : this.#getMonthShort(dateMin);
      return `${prefix} ${dateMin.getDate()} - ${dateMax.getDate()}`;
    }

    return `${this.#formatDate(dateMin, format)} - ${this.#formatDate(dateMax, format)}`;
  }
}

customElements.define('estimated-delivery-component', EstimatedDeliveryComponent);
